import React, { useState } from "react";
import { Modal } from "antd";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import ticketIcon from "@iconify-icons/lucide/ticket";
import logInIcon from "@iconify-icons/lucide/log-in";
import arrowRightIcon from "@iconify-icons/lucide/arrow-right";

import Logo from "../../../resources/images/academyFinalBlue.png";
import useModal from "../../../hooks/useModal";
import useIBO from "../../../hooks/useIBO";
import LoginForm from "../auth/LoginForm";

/**
 * ModalHome — se abre en `/` cuando no hay sesión ni código de IBO en
 * localStorage. Antes tenía dos columnas y la de login solo enrutaba a
 * `/login`; ahora tiene dos tabs y el formulario de login vive dentro
 * (no hay que cambiar de pantalla).
 *
 * Sigue manteniendo el `<Modal>` de antd (compartido con el resto de
 * modales de la app), pero el contenido usa los tokens del sistema.
 */
const ModalHome = () => {
  const { t } = useTranslation("global");
  const { isVisible, toggleModal } = useModal();
  const {
    handleChange,
    validateIBO,
    invalidIbo,
    invalidMessage,
    iboCode,
  } = useIBO(t, toggleModal);

  const [tab, setTab] = useState("login"); // "login" | "code"

  const tabBtn = (active) => ({
    flex: 1,
    minHeight: 44,
    border: 0,
    background: active ? "var(--color-surface)" : "transparent",
    color: active ? "var(--color-ink)" : "var(--color-ink-2)",
    fontSize: 15,
    fontWeight: active ? 800 : 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    borderRadius: 10,
    boxShadow: active ? "0 2px 8px rgba(16, 28, 54, 0.06)" : "none",
    transition: "background .15s ease",
  });

  return (
    <Modal
      open={isVisible}
      footer={false}
      width={480}
      /* Modal bloqueante — se abre cuando NO hay sesión ni código IBO.
         El usuario DEBE elegir una de las dos rutas (login o
         invitado). Sin ellas, la home no tiene sentido. Por eso:
         - `closable={false}` — sin la X propia de antd.
         - `maskClosable={false}` — click en overlay no cierra.
         - `keyboard={false}` — ESC no cierra.
         - Se retira el botón X propio del contenido. */
      closable={false}
      maskClosable={false}
      keyboard={false}
      centered
      styles={{
        content: {
          padding: 0,
          borderRadius: 16,
          overflow: "hidden",
        },
        body: { padding: 0 },
      }}
    >
      <div
        style={{
          background: "var(--color-bg)",
          padding: "22px 22px 22px",
          textAlign: "center",
        }}
      >
        <img
          src={Logo}
          alt="Academy by VisionBiz"
          style={{ height: 40, width: "auto", display: "inline-block" }}
        />
        <p
          className="text-ink-2 m-0"
          style={{
            fontSize: 14,
            fontWeight: 700,
            marginTop: 10,
            lineHeight: 1.45,
          }}
        >
          {t("login.message")}
        </p>
      </div>

      <div
        role="tablist"
        aria-label={t("login.pageTitle", "Iniciar sesión")}
        style={{
          display: "flex",
          gap: 4,
          background: "var(--color-line)",
          padding: 4,
          margin: "16px 22px 0",
          borderRadius: 12,
        }}
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === "login"}
          onClick={() => setTab("login")}
          style={tabBtn(tab === "login")}
        >
          <Icon icon={logInIcon} width={16} height={16} aria-hidden="true" />
          {t("login.pageTitle", "Iniciar sesión")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "code"}
          onClick={() => setTab("code")}
          style={tabBtn(tab === "code")}
        >
          <Icon icon={ticketIcon} width={16} height={16} aria-hidden="true" />
          {t("login.guest", "Invitado")}
        </button>
      </div>

      <div style={{ padding: "20px 22px 24px" }}>
        {tab === "login" ? (
          <LoginForm compact onSuccess={toggleModal} onCancel={toggleModal} />
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              validateIBO(false);
            }}
            noValidate
          >
            <label
              htmlFor="ibo-code-input"
              style={{
                display: "block",
                fontSize: 13,
                fontWeight: 800,
                color: "var(--color-ink)",
                marginBottom: 6,
              }}
            >
              {t("ibo.code")}
            </label>
            <label
              htmlFor="ibo-code-input"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: "var(--color-surface)",
                border: `1.5px solid ${
                  invalidIbo ? "var(--color-danger)" : "var(--color-line-strong)"
                }`,
                borderRadius: 11,
                padding: "0 14px",
                minHeight: 48,
                marginBottom: 10,
              }}
            >
              <Icon
                icon={ticketIcon}
                width={20}
                height={20}
                aria-hidden="true"
                style={{ color: "var(--color-ink-2)", flexShrink: 0 }}
              />
              <input
                id="ibo-code-input"
                name="iboCode"
                type="text"
                required
                placeholder={t("ibo.code")}
                onChange={handleChange}
                aria-invalid={!!invalidIbo}
                aria-describedby={invalidIbo ? "ibo-code-err" : undefined}
                style={{
                  flex: 1,
                  minWidth: 0,
                  border: 0,
                  outline: "none",
                  background: "transparent",
                  fontSize: 16,
                  fontWeight: 600,
                  color: "var(--color-ink)",
                }}
              />
            </label>

            {invalidIbo && (
              <div
                id="ibo-code-err"
                role="alert"
                style={{
                  marginTop: 6,
                  marginBottom: 12,
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: "var(--color-danger)",
                }}
              >
                {invalidMessage}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary w-full flex items-center justify-center gap-s2"
              style={{
                minHeight: 48,
                fontSize: 16,
                fontWeight: 800,
                marginTop: invalidIbo ? 0 : 12,
              }}
            >
              {t("ibo.continue")}
              <Icon
                icon={arrowRightIcon}
                width={18}
                height={18}
                aria-hidden="true"
              />
            </button>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default ModalHome;
